import React, { useEffect, useMemo, useState } from "react";
import { DialogLayout } from "../../../../ui";
import { Button } from "../../../../ui/components/Button";
import { Select } from "../../../../ui/components/Select";
import { TextField } from "../../../../ui/components/TextField";
import { createCurrency } from "../../../../services/api";
import { addableCurrencies, type IsoCurrency } from "./isoCurrencies";

interface CurrencyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Codes already enabled, so the picker cannot offer a duplicate. */
  enabledCodes: Set<string>;
  onCreated: (notice: string) => void;
}

/** Zoho's three options, named the way the dialog asks the question. */
const PRECISIONS: { value: number; label: string; sample: string }[] = [
  { value: 0, label: "None", sample: "1,235" },
  { value: 2, label: "Two", sample: "1,234.57" },
  { value: 3, label: "Three", sample: "1,234.567" },
];

/**
 * Enable a currency.
 *
 * Create only, no edit twin. Everything on a currency row comes from Zoho, so
 * an edit dialog would offer a way to disagree with Zoho and lose on the next
 * sync — the same reasoning as the TCS rate dialog.
 *
 * THE PICKER CARRIES ITS OWN LIST. Zoho tells us which currencies an
 * organisation has, never which ones it could add, so the ISO 4217 list in
 * `isoCurrencies.ts` minus the enabled ones is the only way to ask this
 * question with a dropdown instead of a text box.
 *
 * Decimal places default from ISO rather than from 2. JPY has none and KWD has
 * three; sending 2 for either would store a precision the currency does not
 * have, and precision is not editable afterwards.
 */
export const CurrencyDialog: React.FC<CurrencyDialogProps> = ({
  open,
  onOpenChange,
  enabledCodes,
  onCreated,
}) => {
  const [code, setCode] = useState("");
  const [precision, setPrecision] = useState(2);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(
    () => addableCurrencies(enabledCodes),
    [enabledCodes],
  );

  useEffect(() => {
    if (!open) return;
    setCode("");
    setPrecision(2);
    setError(null);
  }, [open]);

  const picked: IsoCurrency | undefined = options.find((c) => c.code === code);

  const pickCode = (value: string) => {
    setCode(value);
    const iso = options.find((c) => c.code === value);
    if (iso) setPrecision(iso.digits);
  };

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await createCurrency({
        currency_code: code,
        price_precision: precision,
        // A hint only. The backend drops it when blank and Zoho's own default
        // for the code wins; whatever Zoho stores is what gets mirrored back.
        currency_symbol: picked?.symbol,
      });
      onCreated(result.message);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to enable");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-128 max-w-full flex-col items-start gap-6 bg-default-background px-8 py-6">
        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Enable a currency
          </span>
          <span className="text-body font-body text-subtext-color">
            Added to Zoho Books, then mirrored here.
          </span>
        </div>

        <Select
          className="w-full"
          label="Currency"
          placeholder="Select a currency"
          helpText=""
          value={code || ""}
          onValueChange={pickCode}
        >
          {options.map((c) => (
            <Select.Item key={c.code} value={c.code}>
              {`${c.code} — ${c.name}`}
            </Select.Item>
          ))}
        </Select>

        <div className="flex w-full flex-wrap items-start gap-4">
          <Select
            className="grow"
            label="Decimal places"
            placeholder="Two"
            helpText={
              picked && precision !== picked.digits
                ? `${picked.code} normally uses ${picked.digits}`
                : "Cannot be changed once the currency is in use"
            }
            value={String(precision)}
            onValueChange={(value: string) => setPrecision(Number(value))}
          >
            {PRECISIONS.map((p) => (
              <Select.Item key={p.value} value={String(p.value)}>
                {`${p.label} · ${p.sample}`}
              </Select.Item>
            ))}
          </Select>

          <TextField
            className="w-32"
            label="Symbol"
            helpText="Zoho's own"
            disabled
          >
            <TextField.Input value={picked?.symbol ?? ""} onChange={() => {}} />
          </TextField>
        </div>

        {error && (
          <div className="flex w-full items-start rounded-lg border border-solid border-red-200 bg-red-50 p-3">
            <span className="text-body font-body text-red-600">{error}</span>
          </div>
        )}

        <div className="flex w-full items-center justify-end gap-2">
          <Button
            variant="neutral-tertiary"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="brand-primary"
            onClick={handleCreate}
            disabled={saving || code === ""}
          >
            {saving ? "Enabling..." : "Enable in Zoho"}
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};
